import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { type ColumnDef } from '@tanstack/react-table'
import { toast } from 'sonner'
import * as XLSX from 'xlsx'
import { Badge } from '@/components/ui/badge'
import { ExtractionTable } from '@/components/extraction/ExtractionTable'
import { CitationLink } from '@/components/extraction/CitationLink'
import { ColumnsPicker, type ColumnOption } from '@/components/extraction/ColumnsPicker'
import { GroupBySelect, type GroupByOption } from '@/components/extraction/GroupBySelect'
import { StatusDropdown, RISK_STATUS_OPTIONS } from '@/components/extraction/StatusDropdown'
import { OwnerPicker } from '@/components/extraction/OwnerPicker'
import { RiskDrawer, SeverityPill } from '@/components/extraction/RiskDrawer'
import { RowActionsMenu } from '@/components/extraction/RowActionsMenu'
import { AddItemModal, type ModalFieldConfig } from '@/components/extraction/AddItemModal'
import { BulkActionsBar } from '@/components/ui/bulk-actions-bar'
import { BulkConfirmDelete } from '@/components/extraction/BulkConfirmDelete'
import { BulkValuePicker } from '@/components/ui/bulk-value-picker'
import { BulkOwnerPicker } from '@/components/extraction/BulkOwnerPicker'
import { useTableFilterBar } from '@/components/extraction/TableFilterBar'
import { Button } from '@/components/ui/button'
import { MessageSquare, Trash2 } from 'lucide-react'
import { useTableFilters } from '@/hooks/use-table-filters'
import { addItemWithFeedback } from '@/lib/add-item-feedback'
import { buildExportSheet, exportScopeNote } from '@/lib/export-provenance'
import { effectiveRiskSeverity } from '@/lib/risk-severity'
import {
  buildOwnerOptions,
  UNASSIGNED_OWNER,
  type FilterGroups,
  type FilterOptionDef,
} from '@/lib/table-filters'
import {
  readTableView,
  tableViewStorageKey,
  writeTableView,
  type TableViewState,
} from '@/lib/table-view'
import { useAuthStore } from '@/stores/auth-store'
import { useCommentsStore } from '@/stores/comments-store'
import { useExtractionStore } from '@/stores/extraction-store'
import {
  ALL_RISK_DECISIONS,
  RISK_CATEGORY_COLORS,
  RISK_DECISION_LABELS,
  RISK_SEVERITY_LABELS,
  RISK_IMPACT_AREA_LABELS,
  ALL_RISK_CATEGORIES,
  type ExtractedRiskItem,
  type RiskCategory,
  type RiskSeverity,
} from '@/types/extraction'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'
import { formatSourceWithPage } from '@/lib/citation-preview'

// ===================
// Types
// ===================

interface RiskRegisterExtractionProps {
  bidId: string
  readOnly?: boolean
}

interface TeamMember {
  id: string
  userId: string
  fullName: string | null
  avatarUrl: string | null
}

// ===================
// Constants
// ===================

/**
 * The row diet. Eleven columns made a risk unreadable, so the table defaults to
 * the five a bid manager acts on — Risk, Severity, Decision, Owner, Discussion —
 * and everything else is opt-in through the Columns picker.
 *
 * This list is the single source of truth for the picker: a column that is
 * toggleable but not listed here would be unreachable, and one listed but absent
 * from the table would be a dead tick.
 */
const HIDEABLE_COLUMNS: ColumnOption[] = [
  { id: 'number', label: '#' },
  { id: 'category', label: 'Category' },
  { id: 'impact', label: 'Impact' },
  { id: 'clause', label: 'Clause' },
  { id: 'mitigation', label: 'AI suggested mitigation' },
  { id: 'source', label: 'Source' },
  { id: 'status', label: 'Status' },
  { id: 'discussion', label: 'Discussion' },
  { id: 'actions', label: 'Row actions' },
]

/**
 * Hideable and part of the default five — the one column that is both, so the
 * default hidden set can no longer be the whole picker list.
 */
const DEFAULT_VISIBLE_HIDEABLE = new Set(['discussion'])

const DEFAULT_HIDDEN = HIDEABLE_COLUMNS.map((c) => c.id).filter(
  (id) => !DEFAULT_VISIBLE_HIDEABLE.has(id)
)

const DEFAULT_VIEW: TableViewState = { hiddenColumns: DEFAULT_HIDDEN, groupBy: null }

const GROUP_OPTIONS: GroupByOption[] = [
  { value: 'decision', label: 'Decision' },
  { value: 'category', label: 'Category' },
  { value: 'severity', label: 'Severity' },
  { value: 'owner', label: 'Owner' },
]

/** Rows nobody has called yet sort last: they are the pile to work through. */
const NO_DECISION_GROUP = 'No decision'

/** Rows with no owner sort last, under a name no team member can collide with. */
const UNASSIGNED_GROUP = 'Unassigned'

/**
 * Owned, but by someone this bid cannot name — a member whose `fullName` is
 * null, or an owner id no longer on the bid. One bucket, named once: it is both
 * a value the accessor produces and an entry in the group order, so listing it
 * twice would render the group (and its rows) twice.
 */
const UNKNOWN_OWNER_GROUP = 'Unknown'

const SEVERITY_ORDER = Object.keys(RISK_SEVERITY_LABELS) as RiskSeverity[]

// ===================
// Filters
// ===================

/**
 * Derived from the label map, never hand-listed: a new `RiskSeverity` must not
 * be silently unselectable here. `Object.keys` preserves declaration order, so
 * the dropdown reads worst-first (Critical → Low).
 */
export const RISK_SEVERITY_OPTIONS: FilterOptionDef[] = (
  Object.keys(RISK_SEVERITY_LABELS) as RiskSeverity[]
).map((severity) => ({ value: severity, label: RISK_SEVERITY_LABELS[severity] }))

export function buildRiskFilterGroups(
  items: ExtractedRiskItem[],
  teamMembers: Array<{ userId: string; fullName: string | null }>
): FilterGroups<ExtractedRiskItem> {
  return {
    category: {
      label: 'Category',
      options: ALL_RISK_CATEGORIES.map((c) => ({ value: c, label: c })),
      accessor: (r) => r.category,
    },
    severity: {
      label: 'Severity',
      options: RISK_SEVERITY_OPTIONS,
      accessor: (r) => r.severity,
    },
    status: {
      label: 'Status',
      options: RISK_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
      accessor: (r) => r.status,
    },
    owner: {
      label: 'Owner',
      options: buildOwnerOptions(items, teamMembers),
      // Coalesce, or the "Unassigned" option would match no row at all.
      accessor: (r) => r.ownerId ?? UNASSIGNED_OWNER,
      hideWhenSparse: true,
      // Team members arrive asynchronously; without this a restored owner
      // selection would be pruned on first render, before they land.
      skipPrune: true,
      // The selection outlives both sources of options, so it can end up
      // nameable by neither. Without this the chip printed the raw UUID.
      missingLabel: 'Not on this bid',
    },
  }
}

// ===================
// Component
// ===================

export function RiskRegisterExtraction({ bidId, readOnly }: RiskRegisterExtractionProps) {
  const [showAddRow, setShowAddRow] = useState(false)
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [searchParams, setSearchParams] = useSearchParams()
  /** The row whose details the drawer is showing — the id, not the row object. */
  const [activeItemId, setActiveItemId] = useState<string | null>(null)

  const [bulkDelete, setBulkDelete] = useState<{ ids: string[]; aiCount: number } | null>(null)
  const [pendingClear, setPendingClear] = useState<(() => void) | null>(null)

  // Per-user, per-bid view state: which columns are hidden and what the rows
  // are grouped by. Same scoping as the filters — a shared browser must not
  // hand the next account the previous one's column diet.
  const userId = useAuthStore((s) => s.user?.id)
  const viewKey = tableViewStorageKey(userId, bidId, 'risk_register')
  const [viewState, setViewState] = useState<TableViewState>(
    () => readTableView(viewKey) ?? DEFAULT_VIEW
  )

  // Switching bids must reset the view, and React Router reuses this element
  // across `:bidId` changes. Adjusted during render rather than in an effect:
  // an effect would let bid A's view reach the persistence effect under bid B's
  // key, overwriting it.
  const [prevViewKey, setPrevViewKey] = useState(viewKey)
  if (prevViewKey !== viewKey) {
    setPrevViewKey(viewKey)
    setViewState(readTableView(viewKey) ?? DEFAULT_VIEW)
  }

  useEffect(() => {
    writeTableView(viewKey, viewState)
  }, [viewKey, viewState])

  const columnVisibility = useMemo(
    () => Object.fromEntries(viewState.hiddenColumns.map((id) => [id, false])),
    [viewState.hiddenColumns]
  )

  const toggleColumn = useCallback((id: string) => {
    setViewState((prev) => ({
      ...prev,
      hiddenColumns: prev.hiddenColumns.includes(id)
        ? prev.hiddenColumns.filter((c) => c !== id)
        : [...prev.hiddenColumns, id],
    }))
  }, [])

  const setGroupBy = useCallback((value: string | null) => {
    setViewState((prev) => ({ ...prev, groupBy: value }))
  }, [])

  const hiddenColumnSet = useMemo(
    () => new Set(viewState.hiddenColumns),
    [viewState.hiddenColumns]
  )

  // Store
  const categoryState = useExtractionStore((s) => s.categories.risk_register)
  const updateItemStatus = useExtractionStore((s) => s.updateItemStatus)
  const updateItemOwner = useExtractionStore((s) => s.updateItemOwner)
  const addManualItem = useExtractionStore((s) => s.addManualItem)
  const deleteItem = useExtractionStore((s) => s.deleteItem)
  const bulkPatchItems = useExtractionStore((s) => s.bulkPatchItems)
  const bulkDeleteItems = useExtractionStore((s) => s.bulkDeleteItems)

  // Risk threads: one bid-wide count fetch feeds every row badge.
  const countsByItemId = useCommentsStore((s) => s.countsByItemId)
  const loadRequirementCounts = useCommentsStore((s) => s.loadRequirementCounts)

  const items = categoryState.items as ExtractedRiskItem[]

  /**
   * The drawer is driven off the id, not off a captured row: the store hands
   * back a fresh array on every patch, so holding the object would freeze the
   * drawer on a stale copy of the risk the user is editing.
   */
  const activeItem = useMemo(
    () => items.find((i) => i.id === activeItemId) ?? null,
    [items, activeItemId]
  )

  // A risk can leave under the drawer — deleted from the row menu, or dropped
  // by a re-extract. Without this the drawer would sit on nothing.
  useEffect(() => {
    if (activeItemId != null && activeItem == null) setActiveItemId(null)
  }, [activeItemId, activeItem])

  const closeDrawer = useCallback(() => setActiveItemId(null), [])

  const handleDrawerOwnerChange = useCallback(
    (itemId: string, ownerId: string | null) => {
      void updateItemOwner(bidId, 'risk_register', itemId, ownerId)
    },
    [bidId, updateItemOwner]
  )

  const handleDrawerStatusChange = useCallback(
    (itemId: string, status: string) => {
      void updateItemStatus(bidId, 'risk_register', itemId, status)
    },
    [bidId, updateItemStatus]
  )

  /**
   * Thread counts, once the register actually has rows. Gated on `populated`
   * for the same reason the `?item=` link is: firing it against a still-loading
   * category spends a bid-wide query on a table with nothing to badge.
   */
  const isPopulated = categoryState.state === 'populated'
  useEffect(() => {
    if (!isPopulated) return
    void loadRequirementCounts(bidId, 'risk')
  }, [bidId, isPopulated, loadRequirementCounts])

  /**
   * And again when the drawer closes — every close path, which is why this is a
   * transition effect rather than a line in `closeDrawer`: clicking the active
   * row again, Escape, and the row disappearing all close it too. Mutations the
   * user made themselves already nudged the store; this catches the comment
   * someone ELSE left while the drawer was open. One query, never on open.
   */
  const drawerWasOpenRef = useRef(false)
  useEffect(() => {
    if (activeItemId !== null) {
      drawerWasOpenRef.current = true
      return
    }
    if (!drawerWasOpenRef.current) return
    drawerWasOpenRef.current = false
    void loadRequirementCounts(bidId, 'risk')
  }, [activeItemId, bidId, loadRequirementCounts])

  // Fetch team members
  useEffect(() => {
    void api
      .get<{ members: TeamMember[] }>(`/bids/${bidId}/members`)
      .then((data) => setTeamMembers(data.members))
      .catch(() => {
        /* non-critical */
      })
  }, [bidId])

  // Filters — owner options depend on both the rows and the team members
  const filterGroups = useMemo(
    () => buildRiskFilterGroups(items, teamMembers),
    [items, teamMembers]
  )

  const filters = useTableFilters<ExtractedRiskItem>({
    bidId,
    tableKey: 'risk_register',
    groups: filterGroups,
  })

  // Two nodes from one call: the dropdowns go inline in the toolbar, the chip
  // rail goes on its own full-width row beneath it. See `useTableFilterBar`.
  const filterBar = useTableFilterBar({
    groups: filterGroups,
    values: filters.values,
    activeChips: filters.activeChips,
    onToggle: filters.toggle,
    onClear: filters.clear,
    onClearAll: filters.clearAll,
  })

  // Filtered data
  // Hoisted because the hook returns a fresh object every render: depending on
  // `filters` itself would re-run these on every render, while these two members
  // are stable (`clearAll`) or memoised on the values (`matches`).
  const { matches: filtersMatch, clearAll: clearAllFilters } = filters

  const filteredItems = useMemo(() => items.filter(filtersMatch), [items, filtersMatch])

  /**
   * Stats
   *
   * Counted on EFFECTIVE severity — residual where the team has rated what the
   * mitigation leaves, inherent otherwise. A strip still shouting "3 Critical"
   * over risks that have been priced and capped is why nobody reads it.
   */
  const stats = useMemo(() => {
    const total = items.length
    let critical = 0
    let high = 0
    let medium = 0
    let low = 0
    let decided = 0
    for (const item of items) {
      const severity = effectiveRiskSeverity(item)
      if (severity === 'critical') critical++
      else if (severity === 'high') high++
      else if (severity === 'medium') medium++
      else if (severity === 'low') low++
      if (item.riskDecision != null) decided++
    }
    return { total, critical, high, medium, low, decided }
  }, [items])

  /**
   * `?item=<riskId>` — the risk-level deep link a comment notification carries.
   *
   * Unlike the filter params this names ONE row, so it cannot rely on the view
   * it lands in: whatever the user last left stored can be hiding it. The
   * filters are cleared only when the row is actually hidden — an arriving link
   * must not wipe a view it did not need to.
   *
   * Three things this shape is load-bearing about:
   *
   * - It waits for `populated`. Spending the link against a still-loading
   *   category would toast "no longer in this register" about a risk that
   *   simply has not arrived, and spend the link on the way past.
   * - The one-shot ref is what stops the drawer re-opening: the effect re-runs
   *   on every `items` identity change (the store hands back a fresh array on
   *   every patch), and without it closing the drawer would immediately undo
   *   itself.
   * - It is declared AFTER `useTableFilters`, deliberately. Both compute from
   *   the same render's `searchParams` and the later navigation wins; declared
   *   first, this one's deletion of `item` would be overwritten.
   */
  const itemParamConsumedRef = useRef(false)
  useEffect(() => {
    if (itemParamConsumedRef.current) return
    const itemParam = searchParams.get('item')
    if (!itemParam) return
    if (categoryState.state !== 'populated') return
    itemParamConsumedRef.current = true

    const target = items.find((i) => i.id === itemParam)
    if (!target) {
      toast.info('That risk is no longer in this register')
    } else {
      if (!filtersMatch(target)) clearAllFilters()
      setActiveItemId(itemParam)
      // One frame later the widened table has committed, so the row exists.
      requestAnimationFrame(() => {
        document
          .querySelector(`[data-row-id="${CSS.escape(itemParam)}"]`)
          ?.scrollIntoView({ block: 'center' })
      })
    }

    // Spent on both paths — a link left in the URL re-toasts, and survives a
    // reload to re-open a drawer the user has since closed. Only `item` goes:
    // `view=` is the Analysis sub-tab's own param.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('item')
        return next
      },
      { replace: true }
    )
  }, [
    searchParams,
    categoryState.state,
    items,
    filtersMatch,
    clearAllFilters,
    setSearchParams,
  ])

  // Columns
  const columns = useMemo<ColumnDef<ExtractedRiskItem, unknown>[]>(
    () => [
      {
        id: 'number',
        header: '#',
        size: 40,
        cell: ({ row }) => (
          <span className="text-meta">{row.index + 1}</span>
        ),
      },
      {
        accessorKey: 'title',
        header: 'Risk',
        size: 260,
        cell: ({ row }) => {
          const item = row.original
          /**
           * One line: the title, and nothing else.
           *
           * The category dot that used to sit here was colour with no legend —
           * six hues decodable only by hovering each one, which does not exist
           * on touch. Worse, it fought the Severity pill two columns over: red
           * meant "Legal & Liability" on the dot and "critical" on the pill,
           * green meant "Commercial" and "low". A critical commercial risk drew
           * a green dot beside a red pill, on the one screen where red and green
           * have to mean severity. Category is still available as a LABELLED
           * badge in the Columns picker, and the register groups by it.
           *
           * The description was a one-line clamp of prose that opens with
           * boilerplate, so it rarely told two risks apart, and it doubled every
           * row's height. The drawer holds the whole of it, a click away. The
           * title stays hover-titled with the description for a quick peek.
           */
          return (
            <span className="block truncate text-sm" title={item.description || undefined}>
              {item.title}
            </span>
          )
        },
      },
      {
        accessorKey: 'category',
        header: 'Category',
        size: 160,
        cell: ({ row }) => {
          const cat = row.original.category
          const colors = RISK_CATEGORY_COLORS[cat] ?? 'bg-gray-100 text-gray-800'
          return (
            <Badge
              variant="outline"
              className={cn('text-xs px-1.5 py-0 font-normal', colors)}
            >
              {cat}
            </Badge>
          )
        },
      },
      {
        accessorKey: 'severity',
        header: 'Severity',
        size: 140,
        // A record, not a control: the inherent severity is the extraction's
        // finding and the residual is what the mitigation plan leaves. Both are
        // set in the drawer, beside the plan that justifies them.
        cell: ({ row }) => {
          const residual = row.original.residualSeverity ?? null
          return (
            <span className="flex items-center gap-1">
              <SeverityPill severity={row.original.severity} title="Inherent severity" />
              {residual && (
                <>
                  <span aria-hidden="true" className="text-meta">
                    →
                  </span>
                  <SeverityPill severity={residual} title="Residual severity" />
                </>
              )}
            </span>
          )
        },
      },
      {
        id: 'decision',
        header: 'Decision',
        size: 130,
        cell: ({ row }) => {
          const decision = row.original.riskDecision ?? null
          if (!decision) return <span className="text-meta">—</span>
          return (
            <Badge
              variant="secondary"
              dot={false}
              className="whitespace-nowrap px-1.5 py-0 text-xs font-normal"
            >
              {RISK_DECISION_LABELS[decision]}
            </Badge>
          )
        },
      },
      {
        id: 'impact',
        header: 'Impact',
        size: 90,
        cell: ({ row }) => (
          <span className="text-meta">
            {RISK_IMPACT_AREA_LABELS[row.original.impactArea] ?? row.original.impactArea}
          </span>
        ),
      },
      {
        id: 'clause',
        header: 'Clause',
        size: 100,
        cell: ({ row }) => (
          <span className="text-meta">
            {row.original.contractClause ?? '—'}
          </span>
        ),
      },
      {
        id: 'mitigation',
        header: 'AI suggested mitigation',
        size: 180,
        cell: ({ row }) => (
          <span
            className="text-meta line-clamp-2"
            title={row.original.mitigation || undefined}
          >
            {row.original.mitigation ?? '—'}
          </span>
        ),
      },
      {
        id: 'owner',
        header: 'Owner',
        size: 100,
        cell: ({ row }) => (
          <OwnerPicker
            value={row.original.ownerId}
            teamMembers={teamMembers}
            disabled={readOnly ?? false}
            onChange={(userId) => {
              if (row.original.id) {
                void updateItemOwner(
                  bidId,
                  'risk_register',
                  row.original.id,
                  userId
                )
              }
            }}
          />
        ),
      },
      {
        id: 'source',
        header: 'Source',
        size: 70,
        cell: ({ row }) => (
          <CitationLink
            page={row.original.sourcePage}
            documentId={row.original.sourceDocumentId ?? null}
            sourceQuote={row.original.sourceQuote}
            sourceHighlightData={row.original.sourceHighlightData ?? null}
          />
        ),
      },
      {
        id: 'status',
        header: 'Status',
        size: 120,
        cell: ({ row }) => (
          <StatusDropdown
            value={row.original.status || 'identified'}
            options={RISK_STATUS_OPTIONS}
            disabled={readOnly ?? false}
            onChange={(status) => {
              if (row.original.id) {
                void updateItemStatus(
                  bidId,
                  'risk_register',
                  row.original.id,
                  status
                )
              }
            }}
          />
        ),
      },
      {
        id: 'discussion',
        header: 'Discussion',
        size: 60,
        // Deliberately NOT marked `data-no-row-activate`: this cell is a
        // signpost, not a control. Clicking it activates the row like any
        // other, and the drawer is where the thread actually lives.
        cell: ({ row }) => {
          const counts = countsByItemId[row.original.id ?? '']
          const total = counts?.total ?? 0
          const unread =
            !!counts &&
            counts.total > 0 &&
            (counts.watermark === null || counts.lastActivityAt > counts.watermark)
          // An icon-only cell says nothing to a screen reader, and the unread
          // dot is otherwise carried by colour alone.
          const label =
            total === 0
              ? 'No discussion'
              : `${total} comment${total === 1 ? '' : 's'}${unread ? ', unread' : ''}`
          return (
            <span
              aria-label={label}
              title={label}
              className={cn(
                'inline-flex items-center gap-1 text-sm',
                total === 0 ? 'text-subtle' : 'text-muted-foreground'
              )}
            >
              <MessageSquare className="h-4 w-4" />
              {total > 0 && total}
              {unread && (
                <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--brand))]" />
              )}
            </span>
          )
        },
      },
      {
        id: 'actions',
        header: '',
        size: 40,
        cell: ({ row }) => {
          const canDelete =
            !readOnly && row.original.extractionSource === 'manual' && !!row.original.id
          return (
            <RowActionsMenu
              source={row.original.extractionSource ?? 'ai'}
              sourceQuote={row.original.sourceQuote}
              {...(canDelete && {
                onDelete: () =>
                  void deleteItem(bidId, 'risk_register', row.original.id!),
              })}
            />
          )
        },
      },
    ],
    [bidId, teamMembers, updateItemStatus, updateItemOwner, deleteItem, readOnly, countsByItemId]
  )

  /**
   * Render-level grouping. Every accessor returns the LABEL a user reads, not
   * the stored value, so the group header needs no second lookup and the
   * headings match the pills in the rows beneath them.
   */
  const groupByConfig = useMemo(() => {
    const key = viewState.groupBy
    if (!key) return null

    const renderHeader = (groupKey: string, rows: ExtractedRiskItem[]) => (
      <span className="flex items-baseline gap-2">
        <span className="text-sm font-medium">{groupKey}</span>
        <span className="text-meta">{rows.length}</span>
      </span>
    )

    if (key === 'decision') {
      return {
        id: key,
        accessor: (item: ExtractedRiskItem) =>
          item.riskDecision ? RISK_DECISION_LABELS[item.riskDecision] : NO_DECISION_GROUP,
        // Undecided last: it is the pile to work through, not a call anyone made.
        groupOrder: [...ALL_RISK_DECISIONS.map((d) => RISK_DECISION_LABELS[d]), NO_DECISION_GROUP],
        renderHeader,
      }
    }

    if (key === 'category') {
      return {
        id: key,
        accessor: (item: ExtractedRiskItem) => item.category,
        groupOrder: [...ALL_RISK_CATEGORIES],
        renderHeader,
      }
    }

    if (key === 'severity') {
      return {
        id: key,
        // Effective, matching the stats strip: a mitigated risk groups under
        // what it leaves, not what it started as.
        accessor: (item: ExtractedRiskItem) =>
          RISK_SEVERITY_LABELS[effectiveRiskSeverity(item)],
        groupOrder: SEVERITY_ORDER.map((s) => RISK_SEVERITY_LABELS[s]),
        renderHeader,
      }
    }

    const nameOf = new Map(
      teamMembers.map((m) => [m.userId, m.fullName ?? UNKNOWN_OWNER_GROUP] as const)
    )
    // `UNKNOWN_OWNER_GROUP` is excluded here because it is appended below — a
    // member with a null `fullName` folds onto the same bucket as an owner id
    // no member can name, and `ExtractionTable` orders groups with
    // `groupOrder.filter(...)`, which does not dedupe.
    const named = [...new Set(nameOf.values())]
      .filter((name) => name !== UNKNOWN_OWNER_GROUP)
      .sort((a, b) => a.localeCompare(b))
    return {
      id: key,
      accessor: (item: ExtractedRiskItem) =>
        item.ownerId ? (nameOf.get(item.ownerId) ?? UNKNOWN_OWNER_GROUP) : UNASSIGNED_GROUP,
      groupOrder: [...named, UNKNOWN_OWNER_GROUP, UNASSIGNED_GROUP],
      renderHeader,
    }
  }, [viewState.groupBy, teamMembers])

  const toolbarExtras = (
    <>
      <GroupBySelect options={GROUP_OPTIONS} value={viewState.groupBy} onChange={setGroupBy} />
      <ColumnsPicker
        options={HIDEABLE_COLUMNS}
        hidden={hiddenColumnSet}
        onToggle={toggleColumn}
      />
    </>
  )

  // Export
  const handleExport = useCallback(() => {
    // The sheet is the artefact that leaves the app, so it carries what the row
    // diet took off the screen — every severity, both mitigations, and the
    // decision. `Note` is gone: the thread replaced it, and a frozen note
    // string would be stale data on an audit artefact. Counts here;
    // thread-body export is a tracked backlog item.
    const exportData = filteredItems.map((item, i) => ({
      '#': i + 1,
      Risk: item.title,
      Description: item.description || '',
      Category: item.category,
      'Inherent severity': RISK_SEVERITY_LABELS[item.severity] ?? item.severity,
      'Residual severity': item.residualSeverity
        ? (RISK_SEVERITY_LABELS[item.residualSeverity] ?? item.residualSeverity)
        : '',
      'Effective severity': RISK_SEVERITY_LABELS[effectiveRiskSeverity(item)],
      Impact: RISK_IMPACT_AREA_LABELS[item.impactArea] ?? item.impactArea,
      Clause: item.contractClause || '',
      'AI suggested mitigation': item.mitigation || '',
      'Mitigation plan': item.mitigationPlan || '',
      Decision: item.riskDecision ? RISK_DECISION_LABELS[item.riskDecision] : '',
      Status:
        RISK_STATUS_OPTIONS.find((o) => o.value === item.status)?.label ??
        item.status,
      Discussion: (() => {
        const counts = item.id ? countsByItemId[item.id] : undefined
        if (!counts || counts.total === 0) return ''
        return `${counts.total} comment${counts.total === 1 ? '' : 's'} (${counts.open} open)`
      })(),
      Source: item.sourceDocument
        ? formatSourceWithPage(item.sourceDocument, item.sourcePage)
        : '',
    }))
    // The sheet renumbers from 1, so a filtered export is indistinguishable
    // from the full register once forwarded. Filters persist per bid, so the
    // narrowing may have been set weeks ago and forgotten.
    const worksheet = buildExportSheet(
      exportData,
      exportScopeNote({
        chips: filters.activeChips,
        shown: filteredItems.length,
        total: items.length,
      })
    )
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Risk Register')
    XLSX.writeFile(
      workbook,
      `risk-register-${new Date().toISOString().split('T')[0]}.xlsx`
    )
  }, [filteredItems, filters.activeChips, items.length, countsByItemId])

  // Add manual item
  const handleAddManualItem = useCallback(
    async (values: Record<string, string | boolean>) => {
      const asString = (k: string) => {
        const v = values[k]
        return typeof v === 'string' && v ? v : null
      }
      await addItemWithFeedback(
        'Risk',
        () =>
          addManualItem(bidId, 'risk_register', {
            title: asString('title') ?? '',
            description: asString('description') ?? '',
            category: (asString('category') as RiskCategory) || 'Commercial',
            severity: (asString('severity') as RiskSeverity) || 'medium',
            impactArea: (asString('impactArea') as 'financial' | 'schedule' | 'legal' | 'reputation' | 'safety') || 'financial',
            contractClause: asString('contractClause'),
            mitigation: asString('mitigation'),
            status: asString('status') ?? 'identified',
            ownerId: asString('ownerId'),
            sourceQuote: '',
            sourceDocument: '',
            sourcePage: 0,
            extractionSource: 'manual',
          }),
        {
          isVisible: (created) => filtersMatch(created as ExtractedRiskItem),
          onShowItem: clearAllFilters,
        }
      )
    },
    [bidId, addManualItem, filtersMatch, clearAllFilters]
  )

  const modalFields: ModalFieldConfig[] = useMemo(() => [
    { key: 'title', label: 'Risk', type: 'text', required: true, fullWidth: true },
    { key: 'description', label: 'Description', type: 'textarea' },
    { key: 'category', label: 'Category', type: 'select', required: true, options: ALL_RISK_CATEGORIES, defaultValue: 'Commercial' },
    { key: 'severity', label: 'Severity', type: 'select', options: [
      { value: 'critical', label: 'Critical' },
      { value: 'high', label: 'High' },
      { value: 'medium', label: 'Medium' },
      { value: 'low', label: 'Low' },
    ], defaultValue: 'medium' },
    { key: 'impactArea', label: 'Impact area', type: 'select', options: [
      { value: 'financial', label: 'Financial' },
      { value: 'schedule', label: 'Schedule' },
      { value: 'legal', label: 'Legal' },
      { value: 'reputation', label: 'Reputation' },
      { value: 'safety', label: 'Safety' },
    ], defaultValue: 'financial' },
    { key: 'contractClause', label: 'Contract clause', type: 'text' },
    { key: 'mitigation', label: 'Mitigation', type: 'textarea' },
    { key: 'ownerId', label: 'Owner', type: 'owner' },
    // No note field: the thread in the drawer replaced it. No decision or
    // residual severity either — both are calls the team makes after the
    // mitigation plan, which is the drawer's job.
    { key: 'status', label: 'Status', type: 'select', options: RISK_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })), defaultValue: 'identified' },
  ], [])

  // Global filter
  const globalFilterFn = useCallback(
    (row: ExtractedRiskItem, filterValue: string) => {
      const search = filterValue.toLowerCase()
      return (
        row.title.toLowerCase().includes(search) ||
        row.description.toLowerCase().includes(search) ||
        row.category.toLowerCase().includes(search) ||
        (row.contractClause?.toLowerCase().includes(search) ?? false) ||
        (row.mitigation?.toLowerCase().includes(search) ?? false)
      )
    },
    []
  )

  // Height-bounds the docked aside so its body scrolls internally rather than
  // running the page. Below lg the drawer portals into a Sheet and this
  // wrapper renders empty.
  const detailPanel = activeItem ? (
    <div className="sticky top-4 flex max-h-[calc(100vh-10rem)] min-w-[340px] shrink self-start">
      <RiskDrawer
        bidId={bidId}
        item={activeItem}
        onClose={closeDrawer}
        readOnly={readOnly ?? false}
        teamMembers={teamMembers}
        onOwnerChange={handleDrawerOwnerChange}
        onStatusChange={handleDrawerStatusChange}
      />
    </div>
  ) : null

  return (
    <>
    <ExtractionTable<ExtractedRiskItem>
      bidId={bidId}
      category="risk_register"
      title="Risk Register"
      description="Extract contractual, commercial, and delivery risks from tender documents."
      columns={columns}
      data={filteredItems}
      readOnly={readOnly ?? false}
      onExport={handleExport}
      activeRowId={activeItemId}
      onRowActivate={setActiveItemId}
      columnVisibility={columnVisibility}
      toolbarExtras={toolbarExtras}
      groupBy={groupByConfig}
      {...(!readOnly ? { onAddManualItem: () => setShowAddRow(true) } : {})}
      enableSelection={!readOnly}
      renderBulkActions={(rows, clear) => {
        const ids = rows.map((r) => r.id!).filter(Boolean)
        const aiCount = rows.filter((r) => r.extractionSource !== 'manual').length
        return (
          <BulkActionsBar count={rows.length} onClear={clear}>
            <BulkOwnerPicker
              teamMembers={teamMembers}
              onSelect={(userId) => {
                void (async () => {
                  await bulkPatchItems(bidId, 'risk_register', ids, { ownerId: userId })
                  clear()
                })()
              }}
            />
            <BulkValuePicker
              label="Severity"
              options={[
                { value: 'critical', label: 'Critical' },
                { value: 'high', label: 'High' },
                { value: 'medium', label: 'Medium' },
                { value: 'low', label: 'Low' },
              ]}
              onSelect={(severity) => {
                void (async () => {
                  await bulkPatchItems(bidId, 'risk_register', ids, { severity })
                  clear()
                })()
              }}
            />
            <BulkValuePicker
              label="Status"
              options={RISK_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              onSelect={(status) => {
                void (async () => {
                  await bulkPatchItems(bidId, 'risk_register', ids, { status })
                  clear()
                })()
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-background hover:bg-destructive hover:text-destructive-foreground gap-1"
              onClick={() => {
                setBulkDelete({ ids, aiCount })
                setPendingClear(() => clear)
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </Button>
          </BulkActionsBar>
        )
      }}
      globalFilterFn={globalFilterFn}
      filterContent={filterBar.controls}
      filterChips={filterBar.chips}
      statsBar={(visibleCount) => (
        <div className="flex items-center gap-2 text-meta">
          {/* `visibleCount`, not `filteredItems.length`: the search box lives
              inside ExtractionTable, so only it knows what is on screen. The
              per-severity counts below deliberately keep counting the whole
              set — a stat that moves when you filter by it is a bug. */}
          <span>
            {visibleCount === stats.total
              ? `${stats.total} risks`
              : `${visibleCount} of ${stats.total} risks`}
          </span>
          <span>·</span>
          <span className="text-red-600 font-medium">{stats.critical} Critical</span>
          <span>·</span>
          <span className="text-orange-600">{stats.high} High</span>
          <span>·</span>
          <span className="text-amber-600">{stats.medium} Medium</span>
          <span>·</span>
          <span className="text-green-600">{stats.low} Low</span>
          <span>·</span>
          <span className="text-muted-foreground">{stats.decided} decided</span>
        </div>
      )}
      detailPanel={detailPanel}
    />
    <AddItemModal
      open={showAddRow}
      onOpenChange={setShowAddRow}
      title="Add risk"
      description="Add a manual risk to track alongside AI-identified risks."
      fields={modalFields}
      teamMembers={teamMembers}
      onSubmit={handleAddManualItem}
    />
    <BulkConfirmDelete
      open={bulkDelete !== null}
      onOpenChange={(open) => {
        if (!open) {
          setBulkDelete(null)
          setPendingClear(null)
        }
      }}
      count={bulkDelete?.ids.length ?? 0}
      aiCount={bulkDelete?.aiCount ?? 0}
      itemNoun="risk"
      onConfirm={() => {
        void (async () => {
          if (!bulkDelete) return
          await bulkDeleteItems(bidId, 'risk_register', bulkDelete.ids)
          pendingClear?.()
          setBulkDelete(null)
          setPendingClear(null)
        })()
      }}
    />
    </>
  )
}
